/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { FeatureCollection } from 'geojson';
import { useMemo } from 'react';
import {
  fetchGeoJSON,
  fetchItem,
  fetchItems,
  getDataExplorerConfiguration,
} from './api';
import type {
  Concept,
  DataExplorerPresets,
  DiscoveryMetadataFeature,
  DiscoveryMetadataItems,
  Queryable,
} from '../../store/types';

export const useGetDataexplorerConfiguration = (
  productConfigKey: string,
): UseQueryResult<DataExplorerPresets> => {
  return useQuery({
    queryKey: ['dataexplorer-configuration', productConfigKey],
    queryFn: async (): Promise<DataExplorerPresets> =>
      getDataExplorerConfiguration(productConfigKey),
  });
};

export const useFetchItems = (
  baseUrl: string,
  search?: string,
  queryables?: Queryable[],
  concepts?: Concept[],
  bbox?: number[],
  meteogateOnly?: boolean,
  enabled = true,
): UseQueryResult<DiscoveryMetadataItems | null> => {
  const queryableKey = useMemo(() => {
    return queryables
      ?.filter((queryable) => queryable.selected.length > 0)
      .map(
        (queryable) =>
          `${queryable.id} : ${[...queryable.selected].sort().join(',')}`,
      )
      .sort();
  }, [queryables]);

  const conceptKey = useMemo(() => {
    return concepts
      ?.filter((concept) => concept.selected)
      .map((concept) => concept.id)
      .sort();
  }, [concepts]);

  return useQuery({
    queryKey: [
      'fetchItems',
      baseUrl,
      search,
      queryableKey,
      conceptKey,
      bbox,
      meteogateOnly,
    ],
    queryFn: async () =>
      fetchItems(baseUrl, search, queryables, concepts, bbox, meteogateOnly),
    enabled,
  });
};

export const useFetchGeoJSON = (
  collectionUrl: string,
): UseQueryResult<FeatureCollection | null> => {
  return useQuery({
    queryKey: ['fetchGeoJSON', collectionUrl],
    queryFn: async () => fetchGeoJSON(collectionUrl),
    enabled: !!collectionUrl,
  });
};

export const useFetchItem = (
  baseUrl: string,
  datasetId: string,
): UseQueryResult<DiscoveryMetadataFeature | null> => {
  return useQuery({
    queryKey: ['fetchItem', baseUrl, datasetId],
    queryFn: async () => fetchItem(baseUrl, datasetId),
  });
};
